/**
 * Hook for managing streaming chat messages via WebSocket.
 *
 * Encapsulates the WebSocket connection, message state, and streaming
 * lifecycle for both lesson and freeform Learn Anything chat views.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { api, WEBSOCKET_ENDPOINT } from '@/lib/api';
import { authApi } from '@/lib/auth-api';
import { addBreadcrumb } from '@/lib/telemetry';
import { useStreaming } from '@/lib/hooks/useStreaming';
import type { Message } from '../types';

const MAX_CONVERSATION_MESSAGES = 20;

interface StreamingChatOptions {
  /** Extra input fields sent with every startArche call (e.g. courseContext, lessonContext) */
  extraInput?: Record<string, unknown>;
}

export function useStreamingChat(options: StreamingChatOptions = {}) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [currentAssistantMessageId, setCurrentAssistantMessageId] = useState<string | null>(null);
  const authToken = authApi.getIdToken();

  // Keep a stable ref to extraInput so callers can update without re-creating callbacks
  const extraInputRef = useRef(options.extraInput);
  extraInputRef.current = options.extraInput;

  const {
    streamingText,
    isComplete: streamingComplete,
    error: streamingError,
    reset: resetStreaming,
  } = useStreaming({
    requestId: currentRequestId,
    authToken,
    endpoint: WEBSOCKET_ENDPOINT,
    enabled: !!currentRequestId,
  });

  const combinedStreamingText = useMemo(
    () => Object.values(streamingText).join(''),
    [streamingText],
  );

  // Pipe streaming text into the current assistant message
  useEffect(() => {
    if (currentAssistantMessageId && combinedStreamingText) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentAssistantMessageId ? { ...msg, content: combinedStreamingText } : msg,
        ),
      );
    }
  }, [currentAssistantMessageId, combinedStreamingText]);

  // Mark streaming complete
  useEffect(() => {
    if (streamingComplete && currentAssistantMessageId) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentAssistantMessageId ? { ...msg, isStreaming: false } : msg,
        ),
      );
      setIsStreaming(false);
      setCurrentRequestId(null);
      setCurrentAssistantMessageId(null);
      resetStreaming();
    }
  }, [streamingComplete, currentAssistantMessageId, resetStreaming]);

  // Handle streaming errors
  useEffect(() => {
    if (streamingError && currentAssistantMessageId) {
      const errorMessage = streamingError.message.toLowerCase().includes('unauthorized')
        ? 'Your session has expired. Please log out and log back in.'
        : `Error: ${streamingError.message}`;

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentAssistantMessageId
            ? { ...msg, content: errorMessage, isStreaming: false }
            : msg,
        ),
      );
      setIsStreaming(false);
      setCurrentRequestId(null);
      setCurrentAssistantMessageId(null);
      resetStreaming();
    }
  }, [streamingError, currentAssistantMessageId, resetStreaming]);

  const sendMessage = useCallback(
    async (userContent: string) => {
      if (isStreaming) return;

      const userMessageId = `user-${Date.now()}`;
      const assistantMessageId = `assistant-${Date.now()}`;

      const userMessage: Message = { id: userMessageId, role: 'user', content: userContent };
      const assistantMessage: Message = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        isStreaming: true,
      };

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setIsStreaming(true);

      // Build conversation history from messages + the new user message
      const allMessages = [...messages, userMessage].filter((msg) => msg.content.trim().length > 0);
      const truncatedMessages = allMessages.slice(-MAX_CONVERSATION_MESSAGES);
      const conversationHistory = truncatedMessages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      try {
        const result = await api.startArche('arche_learn_anything', {
          messages: conversationHistory,
          provider: 'openai',
          ...extraInputRef.current,
        });

        setCurrentRequestId(result.requestId);
        setCurrentAssistantMessageId(assistantMessageId);
      } catch (err) {
        addBreadcrumb('streaming', 'Streaming failed', { error: String(err) });
        const errorMessage = String(err).toLowerCase().includes('unauthorized')
          ? 'Your session has expired. Please log out and log back in.'
          : 'Sorry, something went wrong. Please try again.';

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessageId
              ? { ...msg, content: errorMessage, isStreaming: false }
              : msg,
          ),
        );
        setIsStreaming(false);
      }
    },
    [isStreaming, messages],
  );

  const stopStreaming = useCallback(() => {
    setCurrentRequestId(null);
    setCurrentAssistantMessageId(null);
    resetStreaming();
    setIsStreaming(false);
    setMessages((prev) =>
      prev.map((msg) => (msg.isStreaming ? { ...msg, isStreaming: false } : msg)),
    );
  }, [resetStreaming]);

  const resetChat = useCallback(() => {
    setMessages([]);
    setCurrentRequestId(null);
    setCurrentAssistantMessageId(null);
    resetStreaming();
    setIsStreaming(false);
  }, [resetStreaming]);

  return {
    messages,
    setMessages,
    isStreaming,
    sendMessage,
    stopStreaming,
    resetChat,
    currentAssistantMessageId,
  };
}
